// ==============================
// Table empty-state helpers
// ==============================
// Shared across every data table so empty/short pages look consistent:
//   - <TableSpacerRow/>: fills the remaining page height with ONE quiet, blank
//     row (no dashes, no internal borders) to keep the table height stable when
//     a page has fewer records than the page size.
//   - <TableNoData/>: a centered "No records found" empty state spanning all
//     columns, shown when a table has zero records.
//
// Both use colSpan={100}; browsers clamp it to the actual column count, so the
// same component works regardless of how many columns a table has.

import { Box, TableCell, TableRow, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { useTranslation } from '../../i18n';

// Approximate height of one dense (size="small") data row, in px.
const DENSE_ROW_HEIGHT = 33;

type TableSpacerRowProps = {
  /** Number of missing rows to pad out. */
  rows: number;
  /** Height of a single row in px (defaults to the dense row height). */
  rowHeight?: number;
};

export const TableSpacerRow = ({
  rows,
  rowHeight = DENSE_ROW_HEIGHT,
}: TableSpacerRowProps) => {
  if (rows <= 0) return null;
  return (
    <TableRow style={{ height: rowHeight * rows }}>
      <TableCell colSpan={100} sx={{ border: 0 }} />
    </TableRow>
  );
};

type TableNoDataProps = {
  message?: string;
};

export const TableNoData = ({ message }: TableNoDataProps) => {
  const { t } = useTranslation();
  // Callers may pass their own message; otherwise use the translated default.
  const text = message ?? t('common.noRecords');
  return (
    <TableRow>
      <TableCell colSpan={100} align="center" sx={{ border: 0, py: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            color: 'text.secondary',
          }}
        >
          <InboxOutlinedIcon sx={{ fontSize: 44, opacity: 0.4 }} />
          <Typography variant="body2" color="text.secondary">
            {text}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};
